import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import api from '../lib/api';
import { isAxiosError } from 'axios';

export interface CompanyMemory {
  companyName?: string;
  website?: string;
  otherNotes?: string[];
  updatedAt?: string; // ISO 8601 timestamp
  createdAt?: string; // ISO 8601 timestamp
}

export interface CompanyMemoryResponse {
  data: CompanyMemory | null;
}

export interface CompanyMemoryUpdateRequest {
  companyName?: string;
  website?: string;
  otherNotes?: string[];
}

export interface CompanyMemoryUpdateResponse {
  data: CompanyMemoryUpdateRequest;
}

// Hook to get company memory
export const useCompanyMemory = (
  options?: UseQueryOptions<CompanyMemory | null, Error>,
  queryKey: string = 'company-memory',
) => {
  return useQuery({
    queryKey: [queryKey],
    queryFn: async (): Promise<CompanyMemory | null> => {
      try {
        const response = await api.get<CompanyMemoryResponse>(
          '/api/core/pleri/company-memory',
        );
        return response.data.data;
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response?.status === 404) {
            return null;
          }
        }
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

// Hook to update company memory
export const useUpdateCompanyMemory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      params: CompanyMemoryUpdateRequest,
    ): Promise<CompanyMemoryUpdateRequest> => {
      // Convert empty string to undefined for website (per spec)
      const requestData: CompanyMemoryUpdateRequest = {
        ...params,
        website: params.website === '' ? undefined : params.website,
      };

      const response = await api.put<CompanyMemoryUpdateResponse>(
        '/api/core/pleri/company-memory',
        requestData,
      );

      // Return the updated data from response
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-memory'] });
    },
  });
};
